import { FC, useRef, useState } from "react"
import {
    Avatar, Box, ClickAwayListener, Divider, Grow, IconButton,
    Paper, Popper, Typography
} from "@mui/material"
import { LogoutOutlined, Star } from "@mui/icons-material"
import { getImageUrl } from "../../helpers"
import { UserProps } from "../../types/user"

interface Props {
    User?: UserProps
    onLogout: () => void
}

const initials = (first = '', last = '') => `${first[0] || ''}${last[0] || ''}`.toUpperCase()

const ProfileMenu: FC<Props> = ({ User, onLogout }) => {
    const [open, setOpen] = useState(false)
    const anchorRef = useRef<HTMLButtonElement>(null)

    const overallRating = User
        ? Math.round((((User.propertyRating || 0) + (User.gigRating || 0) + (User.shopRating || 0)) / 3) * 10) / 10
        : 0
    const photoUrl = getImageUrl(User?.profilePhotos, 'small')
    const displayName = `${User?.firstName || ''} ${User?.lastName || ''}`.trim()

    return (
        <Box>
            <IconButton ref={anchorRef} onClick={() => setOpen(p => !p)} size="small">
                <Avatar src={photoUrl} sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 700 }}>
                    {initials(User?.firstName, User?.lastName)}
                </Avatar>
            </IconButton>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                transition
                disablePortal
                sx={{ zIndex: 1300 }}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
                        <Paper sx={{ mt: 1, width: 240, borderRadius: 3, overflow: 'hidden' }}>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, px: 2 }}>
                                        <Avatar src={photoUrl} sx={{ width: 64, height: 64, fontSize: 22, fontWeight: 700, mb: 1.5 }}>
                                            {initials(User?.firstName, User?.lastName)}
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 800, fontSize: 15 }} noWrap>
                                            {displayName || '—'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.3 }} noWrap>
                                            @{User?.userName || '—'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 1 }}>
                                            <Star sx={{ fontSize: 16, color: '#c4aa14' }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                                {overallRating.toFixed(1)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider />
                                    <Box
                                        onClick={() => { setOpen(false); onLogout() }}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.2, px: 2.5, py: 1.6, cursor: 'pointer',
                                            '&:hover': { bgcolor: '#f8fafc' }
                                        }}
                                    >
                                        <LogoutOutlined sx={{ fontSize: 18, color: '#64748b' }} />
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Logout</Typography>
                                    </Box>
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Box>
    )
}

export default ProfileMenu
